/**
 * Browser-side JWT decoder. Decode-only — signature verification happens
 * server-side (see PR-1's StrictJwtVerifier in the backend). The browser
 * uses claims for routing decisions ("is this user a platform admin?")
 * but NEVER for authorisation; every protected API call still goes
 * through the bearer token + server check.
 *
 * If the token is malformed we return null and let the caller treat it
 * as "no claims" — UI falls back to the public route.
 */

export interface JwtClaims {
  /** Subject — usually the user id. */
  sub?: string;
  /** Free-form role label. Backend recognises PRODUCT_OWNER + PLATFORM_ADMIN as admin. */
  role?: string;
  /** Tenant the user belongs to. May be absent for cross-tenant operators. */
  tenantId?: string;
  /** Tenant organization display name (set on tenant-user logins). */
  organizationName?: string;
  /** Login username (set on tenant-user logins). */
  username?: string;
  /** Display name (full_name on tenant_user, name on app_user). */
  name?: string;
  /** Issued-at and expiry, unix seconds. */
  iat?: number;
  exp?: number;
  /** Anything else the issuer adds — kept opaque so we don't need to update the type for every new claim. */
  [key: string]: unknown;
}

const PLATFORM_ADMIN_ROLES = new Set(['PRODUCT_OWNER', 'PLATFORM_ADMIN']);

export function decodeJwt(token: string | null | undefined): JwtClaims | null {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const payload = parts[1];
    if (!payload) return null;
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json) as JwtClaims;
  } catch {
    return null;
  }
}

export function isExpired(claims: JwtClaims | null, nowSeconds = Date.now() / 1000): boolean {
  if (!claims?.exp) return false; // no exp → trust until something else says otherwise
  return claims.exp <= nowSeconds;
}

export function isPlatformAdmin(claims: JwtClaims | null): boolean {
  return !!claims?.role && PLATFORM_ADMIN_ROLES.has(claims.role);
}
